import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import apiService from '../../services/apiService';

const DRDocsTab3 = () => {
  const { id } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add state for document description modal
  const [documentModal, setDocumentModal] = useState({
    isOpen: false,
    item: null,
    title: '',
    description: ''
  });

  // Add state for information query modal
  const [informationModal, setInformationModal] = useState({
    isOpen: false,
    item: null,
    title: '',
    body: ''
  });

  useEffect(() => {
    if (id) {
      fetchExtDDItems();
    }
  }, [id]);

  const fetchExtDDItems = async () => {
    try {
      setLoading(true);
      
      const data = await apiService.get(`/property-data-room/extdd?propertyid=${id}`);
      
      setItems(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching external due diligence items:', err);
      setError('Failed to load external due diligence data room items');
    } finally {
      setLoading(false);
    }
  };

  const handleExtDDDocument = (item) => {
    setDocumentModal({
      isOpen: true,
      item: item,
      title: '',
      description: ''
    });
  };

  const handleExtDDInformation = (item) => {
    setInformationModal({
      isOpen: true,
      item: item,
      title: '',
      body: ''
    });
  };

  const handleSubmitDocumentRequest = async () => {
    if (!documentModal.item || documentModal.title.trim().length === 0) {
      alert('Please provide a document title.');
      return;
    }

    if (documentModal.description.trim().length === 0) {
      alert('Please provide a document description.');
      return;
    }

    try {
      const requestBody = {
        documenttitle: documentModal.title.trim(),
        documentdescription: documentModal.description.trim(),
        propertyDataRoomId: documentModal.item.id,
        requesttype: "file"
      };

      console.log('Submitting document request:', requestBody);

      await apiService.post('/property-data-room/addextDDrequest', requestBody);
      
      alert('Document request submitted successfully!');
      
      // Close modal and reset
      setDocumentModal({ isOpen: false, item: null, title: '', description: '' });
      
    } catch (err) {
      console.error('Error submitting document request:', err);
      alert('Failed to submit document request. Please try again.');
    }
  };

  const handleSubmitInformationRequest = async () => {
    if (!informationModal.item || informationModal.title.trim().length === 0) {
      alert('Please provide a query title.');
      return;
    }

    if (informationModal.body.trim().length === 0) {
      alert('Please provide a query description.');
      return;
    }

    try {
      const requestBody = {
        queryTitle: informationModal.title.trim(),
        queryBody: informationModal.body.trim(),
        propertyDataRoomId: informationModal.item.id,
        requesttype: "text"
      };

      console.log('Submitting information request:', requestBody);

      await apiService.post('/property-data-room/addextDDrequest', requestBody);
      
      alert('Information request submitted successfully!');
      
      // Close modal and reset
      setInformationModal({ isOpen: false, item: null, title: '', body: '' });
      
    } catch (err) {
      console.error('Error submitting information request:', err);
      alert('Failed to submit information request. Please try again.');
    }
  };

  const renderItem = (item, index, itemsList) => {
    const isDataRoom = item.itemType === 'DATA_ROOM';
    let indentLevel;
    
    if (isDataRoom) {
      // Level headings use their level for indentation
      indentLevel = item.level - 1;
    } else {
      // For documents, find their parent using checkListId instead of id
      const parentItem = itemsList.find(parent => 
        parent.checkListId === item.parentId && parent.itemType === 'DATA_ROOM'
      );
      
      if (parentItem) {
        // Calculate parent's indentation position
        const parentIndentLevel = parentItem.level - 1;
        // Document is indented TWO levels more than its parent's position
        indentLevel = parentIndentLevel + 2;
      } else {
        // Fallback: If parent not found, use a reasonable default indentation
        indentLevel = Math.max(2, (item.level || 1) + 1);
      }
    }
    
    if (isDataRoom) {
      // Render as a level heading - NO action buttons
      return (
        <div 
          key={`${item.id}-${index}`}
          className="data-room-item heading"
          style={{ 
            marginLeft: `${indentLevel * 20}px`,
            textDecoration: item.isVisible === false ? 'line-through' : 'none',
            opacity: item.isVisible === false ? 0.6 : 1,
            borderLeft: item.isVisible === false ? '4px solid red' : '2px solid green',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div className="item-header" style={{ flex: 1 }}>
            <span className="item-name">{item.displayName}</span>
          </div>
          <div className="extdd-column" style={{ 
            width: '80px', 
            display: 'flex', 
            gap: '4px', 
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            {item.level >= 1 && item.level <= 4 && (
              <>
                <button 
                  className="extdd-document-icon"
                  onClick={() => handleExtDDDocument(item)}
                  title="Describe document required"
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    fontSize: '16px',
                    cursor: 'pointer',
                    color: '#0066cc',
                    padding: '4px'
                  }}
                >
                  📄
                </button>
                <button 
                  className="extdd-information-icon"
                  onClick={() => handleExtDDInformation(item)}
                  title="Describe information required"
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    fontSize: '16px',
                    cursor: 'pointer',
                    color: '#0066cc',
                    padding: '4px'
                  }}
                >
                  ℹ️
                </button>
              </>
            )}
          </div>
        </div>
      );
    } else {
      // Render as a downloadable file - NO action buttons
      return (
        <div 
          key={`${item.id}-${index}`}
          className="data-room-item file"
          style={{ 
            marginLeft: `${indentLevel * 20}px`,
            textDecoration: item.isVisible === false ? 'line-through' : 'none',
            opacity: item.isVisible === false ? 0.6 : 1,
            borderLeft: item.isVisible === false ? '4px solid red' : '2px solid green',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div className="item-header" style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
              {item.isPublished && (
                <span style={{ color: 'green', fontSize: '16px' }}>▶</span>
              )}
              {item.isRejected && (
                <>
                  <span style={{ color: 'red', fontSize: '16px' }}>⚠️</span>
                  <span
                    style={{ 
                      color: 'red', 
                      fontSize: '14px',
                      cursor: 'help'
                    }}
                    title={item.rejectedReason || 'No reason provided'}
                  >
                    ℹ️
                  </span>
                </>
              )}
              <span className="item-name">{item.displayName}</span>
            </div>
          </div>
          <div className="extdd-column" style={{ 
            width: '80px', 
            display: 'flex', 
            gap: '4px', 
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            {item.level >= 1 && item.level <= 4 && (
              <>
                <button 
                  className="extdd-document-icon"
                  onClick={() => handleExtDDDocument(item)}
                  title="Describe document required"
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    fontSize: '16px',
                    cursor: 'pointer',
                    color: '#0066cc',
                    padding: '4px'
                  }}
                >
                  📄
                </button>
                <button 
                  className="extdd-information-icon"
                  onClick={() => handleExtDDInformation(item)}
                  title="Describe information required"
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    fontSize: '16px',
                    cursor: 'pointer',
                    color: '#0066cc',
                    padding: '4px'
                  }}
                >
                  ℹ️
                </button>
              </>
            )}
          </div>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="tab-content">
        <div className="loading-state">
          <p>Loading external due diligence data room items...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tab-content">
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchExtDDItems} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="tab-content">
        <h3>External Due Diligence</h3>
        <p>No external due diligence data room items found for this property.</p>
      </div>
    );
  }

  return (
    <div className="tab-content">
      <h3>External Due Diligence</h3>
      
      <div className="data-room-container" style={{ padding: '16px' }}>
        {items
          .filter(item => item.isVisible !== false)
          .map((item, index) => renderItem(item, index, items))}
      </div>

      {/* Document Request Modal */}
      {documentModal.isOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--cbre-bg-primary)',
            padding: '24px',
            borderRadius: '8px',
            border: '1px solid var(--cbre-border)',
            minWidth: '400px',
            maxWidth: '600px'
          }}>
            <h4 style={{ 
              margin: '0 0 16px 0',
              color: 'var(--cbre-text)'
            }}>
              Describe Document Required: {documentModal.item?.displayName}
            </h4>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block',
                marginBottom: '8px',
                color: 'var(--cbre-text)',
                fontWeight: 'bold'
              }}>
                Document Title:
              </label>
              <input
                type="text"
                value={documentModal.title}
                onChange={(e) => setDocumentModal({...documentModal, title: e.target.value})}
                placeholder="Enter document title..."
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--cbre-border)',
                  borderRadius: '4px',
                  backgroundColor: 'var(--cbre-bg-secondary)',
                  color: 'var(--cbre-text)',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block',
                marginBottom: '8px',
                color: 'var(--cbre-text)',
                fontWeight: 'bold'
              }}>
                Document Description:
              </label>
              <textarea
                value={documentModal.description}
                onChange={(e) => setDocumentModal({...documentModal, description: e.target.value})}
                placeholder="Describe the document required..."
                style={{
                  width: '100%',
                  height: '120px',
                  padding: '12px',
                  border: '1px solid var(--cbre-border)',
                  borderRadius: '4px',
                  backgroundColor: 'var(--cbre-bg-secondary)',
                  color: 'var(--cbre-text)',
                  fontSize: '14px',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDocumentModal({ isOpen: false, item: null, title: '', description: '' })}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--cbre-bg-secondary)',
                  border: '1px solid var(--cbre-border)',
                  borderRadius: '4px',
                  color: 'var(--cbre-text)',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitDocumentRequest}
                disabled={documentModal.title.trim().length === 0 || documentModal.description.trim().length === 0}
                style={{
                  padding: '8px 16px',
                  backgroundColor: (documentModal.title.trim().length === 0 || documentModal.description.trim().length === 0) ? 'var(--cbre-bg-secondary)' : '#0066cc',
                  border: '1px solid var(--cbre-border)',
                  borderRadius: '4px',
                  color: (documentModal.title.trim().length === 0 || documentModal.description.trim().length === 0) ? 'var(--cbre-text-secondary)' : 'white',
                  cursor: (documentModal.title.trim().length === 0 || documentModal.description.trim().length === 0) ? 'not-allowed' : 'pointer'
                }}
              >
                Submit Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Information Request Modal */}
      {informationModal.isOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--cbre-bg-primary)',
            padding: '24px',
            borderRadius: '8px',
            border: '1px solid var(--cbre-border)',
            minWidth: '400px',
            maxWidth: '600px'
          }}>
            <h4 style={{ 
              margin: '0 0 16px 0',
              color: 'var(--cbre-text)'
            }}>
              Describe Information Required: {informationModal.item?.displayName}
            </h4>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block',
                marginBottom: '8px',
                color: 'var(--cbre-text)',
                fontWeight: 'bold'
              }}>
                Query Title:
              </label>
              <input
                type="text"
                value={informationModal.title}
                onChange={(e) => setInformationModal({...informationModal, title: e.target.value})}
                placeholder="Enter query title..."
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--cbre-border)',
                  borderRadius: '4px',
                  backgroundColor: 'var(--cbre-bg-secondary)',
                  color: 'var(--cbre-text)',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block',
                marginBottom: '8px',
                color: 'var(--cbre-text)',
                fontWeight: 'bold'
              }}>
                Query Description:
              </label>
              <textarea
                value={informationModal.body}
                onChange={(e) => setInformationModal({...informationModal, body: e.target.value})}
                placeholder="Describe the information required..."
                style={{
                  width: '100%',
                  height: '120px',
                  padding: '12px',
                  border: '1px solid var(--cbre-border)',
                  borderRadius: '4px',
                  backgroundColor: 'var(--cbre-bg-secondary)',
                  color: 'var(--cbre-text)',
                  fontSize: '14px',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setInformationModal({ isOpen: false, item: null, title: '', body: '' })}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--cbre-bg-secondary)',
                  border: '1px solid var(--cbre-border)',
                  borderRadius: '4px',
                  color: 'var(--cbre-text)',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitInformationRequest}
                disabled={informationModal.title.trim().length === 0 || informationModal.body.trim().length === 0}
                style={{
                  padding: '8px 16px',
                  backgroundColor: (informationModal.title.trim().length === 0 || informationModal.body.trim().length === 0) ? 'var(--cbre-bg-secondary)' : '#0066cc',
                  border: '1px solid var(--cbre-border)',
                  borderRadius: '4px',
                  color: (informationModal.title.trim().length === 0 || informationModal.body.trim().length === 0) ? 'var(--cbre-text-secondary)' : 'white',
                  cursor: (informationModal.title.trim().length === 0 || informationModal.body.trim().length === 0) ? 'not-allowed' : 'pointer'
                }}
              >
                Submit Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DRDocsTab3;
